import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  }), withCredentials: true
};

export interface IRoleGrant {
  user_id: number;
  location_id: number;
  role_id: number;
}

export interface IGroupLocation {
  group_id: number;
  location_id: number;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Server-authoritative admin check. Calls `am-i-admin`, which resolves the
   * user from the authenticated token server-side — no client-supplied id — so
   * it can't be spoofed by editing the otd-user cookie. 200 => admin; any error
   * (403/network/expired token) => false (fail-closed).
   */
  checkAdmin(): Observable<boolean> {
    return this.http.get(`${this.apiUrl}/am-i-admin`, httpOptions).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /** Revoke the current access token server-side. */
  logout(): Observable<any> {
    return this.http.get(`${this.apiUrl}/logout`, httpOptions);
  }

  getUsers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/user-list`, httpOptions);
  }

  createUser(payload: { name: string; email: string; password: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, payload, httpOptions);
  }

  updateUser(payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/update-profile`, payload, httpOptions);
  }

  deleteUser(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/users/${id}`, httpOptions);
  }

  getRoles(): Observable<any> {
    return this.http.get(`${this.apiUrl}/roles`, httpOptions);
  }

  getLocations(): Observable<any> {
    return this.http.get(`${this.apiUrl}/locations`, httpOptions);
  }

  assignRole(grant: IRoleGrant): Observable<any> {
    return this.http.post(`${this.apiUrl}/user-roles`, grant, httpOptions);
  }

  removeRole(grant: IRoleGrant): Observable<any> {
    // DELETE with a body — HttpClient.request lets us send one.
    return this.http.request('delete', `${this.apiUrl}/user-roles`, {
      body: grant,
      headers: httpOptions.headers,
      withCredentials: true
    });
  }

  // --- Groups ---

  getGroups(): Observable<any> {
    return this.http.get(`${this.apiUrl}/groups`, httpOptions);
  }

  getGroup(id: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/groups/${id}`, httpOptions);
  }

  createGroup(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/groups`, payload, httpOptions);
  }

  updateGroup(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/groups/${id}`, payload, httpOptions);
  }

  deleteGroup(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/groups/${id}`, httpOptions);
  }

  getGroupLocations(id: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/groups/${id}/locations`, httpOptions);
  }

  assignLocation(link: IGroupLocation): Observable<any> {
    return this.http.post(`${this.apiUrl}/group-locations`, link, httpOptions);
  }

  removeLocation(link: IGroupLocation): Observable<any> {
    return this.http.request('delete', `${this.apiUrl}/group-locations`, {
      body: link,
      headers: httpOptions.headers,
      withCredentials: true
    });
  }

  // --- Locations ---

  getLocation(id: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/locations/${id}`, httpOptions);
  }

  createLocation(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/locations`, payload, httpOptions);
  }

  updateLocation(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/locations/${id}`, payload, httpOptions);
  }

  deleteLocation(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/locations/${id}`, httpOptions);
  }
}
